import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar } from "@/components/AppTopBar";

const POS_REASONS = ["FOUND_STOCK", "PREVIOUS_COUNT_ERROR", "UNRECORDED_INBOUND", "OTHER"];
const NEG_REASONS = ["MISSING_STOCK", "PREVIOUS_COUNT_ERROR", "UNRECORDED_OUTBOUND", "SHRINKAGE", "OTHER"];
const REASON_LABELS = {
  FOUND_STOCK: "Stok Ditemukan",
  PREVIOUS_COUNT_ERROR: "Kesalahan Hitung Sebelumnya",
  UNRECORDED_INBOUND: "Barang Masuk Belum Tercatat",
  MISSING_STOCK: "Stok Tidak Ditemukan",
  UNRECORDED_OUTBOUND: "Barang Keluar Belum Tercatat",
  SHRINKAGE: "Selisih / Penyusutan Stok",
  OTHER: "Lainnya",
};
const reasonLabel = (code) => REASON_LABELS[code] || code || "-";

export default function StockOpname() {
  const { user } = useAuth();
  const canView = user && (user.role === "OWNER" || user.role === "ADMIN_INVENTORY");
  const [list, setList] = useState([]);
  const [current, setCurrent] = useState(null);
  const [products, setProducts] = useState([]);
  const [invMap, setInvMap] = useState({});
  const [addForm, setAddForm] = useState({ product_id: "", physical_qty: "", reason: "", note: "" });
  const [error, setError] = useState("");
  const [noBasisSkus, setNoBasisSkus] = useState([]);

  const loadList = useCallback(() => {
    api.get("/inventory/opnames").then(({ data }) => setList(data)).catch(() => {});
    api.get("/products").then(({ data }) => setProducts(data.filter((p) => p.status === "ACTIVE"))).catch(() => {});
    api.get("/inventory").then(({ data }) => {
      const m = {}; data.forEach((r) => { m[r.product_id || r.id] = r; }); setInvMap(m);
    }).catch(() => {});
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const openOpname = (id) => api.get(`/inventory/opnames/${id}`).then(({ data }) => setCurrent(data)).catch(() => {});

  const createDraft = async () => {
    setError("");
    try { const { data } = await api.post("/inventory/opnames", {}); setCurrent(data); loadList(); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const addItem = async () => {
    setError("");
    if (!addForm.product_id || addForm.physical_qty === "") { setError("SKU dan qty fisik wajib diisi."); return; }
    try {
      const { data } = await api.post(`/inventory/opnames/${current.id}/items`, {
        product_id: addForm.product_id, physical_qty: parseInt(addForm.physical_qty, 10),
        reason: addForm.reason || null, note: addForm.note || null,
      });
      setCurrent(data); setAddForm({ product_id: "", physical_qty: "", reason: "", note: "" });
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const editItemQty = async (it) => {
    setError("");
    const input = window.prompt(`Ubah qty fisik untuk ${it.sku_code} (system ${it.system_qty_snapshot}):`, String(it.physical_qty));
    if (input === null) return;
    const phys = parseInt(input, 10);
    if (Number.isNaN(phys) || phys < 0) { setError("Qty fisik tidak valid (integer >= 0)."); return; }
    const variance = phys - it.system_qty_snapshot;
    let reason = it.reason || null;
    if (variance !== 0 && !reason) {
      reason = window.prompt("Alasan selisih (mis. FOUND_STOCK / MISSING_STOCK / PREVIOUS_COUNT_ERROR / OTHER):", "OTHER") || null;
    }
    try {
      const { data } = await api.patch(`/inventory/opnames/${current.id}/items/${it.id}`, { physical_qty: phys, reason });
      setCurrent(data); setNoBasisSkus([]);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const deleteItem = async (it) => {
    setError("");
    if (!window.confirm(`Hapus baris ${it.sku_code} dari opname ini? Tidak ada efek stok/valuasi.`)) return;
    try {
      const { data } = await api.delete(`/inventory/opnames/${current.id}/items/${it.id}`);
      setCurrent(data); setNoBasisSkus([]);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const postOpname = async () => {
    setError(""); setNoBasisSkus([]);
    const items = current.items || [];
    if (items.some((i) => i.is_component)) {
      setError("Stock Opname berisi SKU komponen/turunan. Hapus baris tersebut sebelum posting; SKU turunan tidak boleh disesuaikan.");
      return;
    }
    const pos = items.filter((i) => i.variance_qty > 0).length;
    const neg = items.filter((i) => i.variance_qty < 0).length;
    if (!window.confirm(`Stock Opname akan mengubah stok GOOD sesuai selisih fisik.\nPerubahan akan tercatat di riwayat dan tidak mengubah DAMAGED atau Finance.\n\nTotal SKU: ${items.length} | Selisih +: ${pos} | Selisih -: ${neg}`)) return;
    try { const { data } = await api.post(`/inventory/opnames/${current.id}/post`); setCurrent(data); loadList(); }
    catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "MISSING_GOOD_COST_BASIS") {
        setNoBasisSkus((d.offenders || []).map((o) => o.sku).filter(Boolean));
        setError(d.message || "HPP / cost basis GOOD tidak ditemukan.");
      } else {
        setNoBasisSkus([]);
        setError(formatApiErrorDetail(d) || err.message);
      }
    }
  };

  if (user && !canView) {
    return <div><AppTopBar active="opname" /><main className="page"><p className="hint">Akses ditolak.</p></main></div>;
  }

  const isDraft = current && current.status === "DRAFT";
  return (
    <div>
      <AppTopBar active="opname" />
      <main className="page" style={{ maxWidth: 1320, margin: "0 auto" }}>
        <div className="page-head"><h1 className="page-title">Stock Opname</h1>
          <button className="btn-primary" onClick={createDraft} data-testid="opname-create-button">Buat Opname Baru</button>
        </div>
        {error && <div className="alert-error" data-testid="opname-error">{error}</div>}

        {!current && (
          <section className="panel" data-testid="opname-list-panel">
            <h2 className="panel-title">Daftar Opname ({list.length})</h2>
            <div className="table-scroll">
              <table className="data-table" data-testid="opname-list-table">
                <thead><tr><th>Opname No</th><th>Status</th><th>Tanggal</th><th></th></tr></thead>
                <tbody>
                  {list.length === 0 && <tr><td colSpan={4} className="empty-cell">Belum ada opname.</td></tr>}
                  {list.map((o) => (
                    <tr key={o.id} data-testid={`opname-row-${o.opname_number}`}>
                      <td className="mono">{o.opname_number}</td>
                      <td><span className={`status-pill ${o.status === "POSTED" ? "on" : "off"}`}>{o.status}</span></td>
                      <td>{new Date(o.created_at).toLocaleString("id-ID")}</td>
                      <td><button className="btn-mini" onClick={() => openOpname(o.id)} data-testid={`opname-open-${o.opname_number}`}>Buka</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {current && (
          <section className="panel" data-testid="opname-detail-panel">
            <div className="page-head">
              <h2 className="panel-title">{current.opname_number} — <span className={`status-pill ${current.status === "POSTED" ? "on" : "off"}`} data-testid="opname-status">{current.status}</span></h2>
              <button className="btn-ghost" onClick={() => { setCurrent(null); loadList(); }} data-testid="opname-back-button">Kembali</button>
            </div>

            {isDraft && (() => {
              const selInv = invMap[addForm.product_id];
              const sysQty = selInv ? (selInv.available_qty ?? selInv.good_qty ?? null) : null;
              const phys = addForm.physical_qty === "" ? null : parseInt(addForm.physical_qty, 10);
              const prevVar = (sysQty != null && phys != null && !Number.isNaN(phys)) ? phys - sysQty : null;
              const reasonOpts = prevVar > 0 ? POS_REASONS : prevVar < 0 ? NEG_REASONS : [];
              return (
              <div className="panel" style={{ marginTop: 8 }} data-testid="opname-additem-form">
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
                  <div style={{ minWidth: 220 }}>
                    <label className="field-label">SKU</label>
                    <select className="field-input" value={addForm.product_id}
                      onChange={(e) => setAddForm({ ...addForm, product_id: e.target.value, reason: "" })} data-testid="opname-product-select">
                      <option value="">-- pilih SKU --</option>
                      {products.filter((p) => invMap[p.id]?.stock_source !== "COMPONENT").map((p) => <option key={p.id} value={p.id}>{p.sku_code} — {p.description}</option>)}
                    </select>
                  </div>
                  <div style={{ width: 120 }}>
                    <label className="field-label">Qty Fisik{sysQty != null ? ` (sys ${sysQty})` : ""}</label>
                    <input className="field-input" type="number" min="0" placeholder="qty" value={addForm.physical_qty}
                      onChange={(e) => setAddForm({ ...addForm, physical_qty: e.target.value, reason: "" })} data-testid="opname-physical-input" />
                  </div>
                  <div style={{ width: 220 }}>
                    <label className="field-label">Alasan{prevVar ? ` (selisih ${prevVar > 0 ? "+" : ""}${prevVar})` : ""}</label>
                    <select className="field-input" value={addForm.reason} disabled={!prevVar}
                      onChange={(e) => setAddForm({ ...addForm, reason: e.target.value })} data-testid="opname-reason-select">
                      <option value="">{prevVar ? "-- pilih alasan --" : "(tidak perlu)"}</option>
                      {reasonOpts.map((r) => <option key={r} value={r}>{reasonLabel(r)}</option>)}
                    </select>
                  </div>
                  <div style={{ width: 200 }}>
                    <label className="field-label">Catatan (opsional)</label>
                    <input className="field-input" placeholder="catatan" value={addForm.note}
                      onChange={(e) => setAddForm({ ...addForm, note: e.target.value })} data-testid="opname-note-input" />
                  </div>
                  <button className="btn-primary" onClick={addItem} data-testid="opname-additem-button">Tambah SKU</button>
                </div>
              </div>
              );
            })()}

            <div className="table-scroll" style={{ marginTop: 8 }}>
              <table className="data-table" data-testid="opname-items-table">
                <thead><tr><th>SKU</th><th>Deskripsi</th><th>System</th><th>Fisik</th><th>Selisih</th><th>Alasan</th><th>Catatan</th>{isDraft && <th>Aksi</th>}</tr></thead>
                <tbody>
                  {(current.items || []).length === 0 && <tr><td colSpan={isDraft ? 8 : 7} className="empty-cell">Belum ada SKU.</td></tr>}
                  {(current.items || []).map((it) => {
                    const noBasis = noBasisSkus.includes(it.sku_code);
                    return (
                    <tr key={it.id} data-testid={`opname-item-${it.sku_code}`} className={(it.is_component || noBasis) ? "row-invalid" : undefined} style={it.is_component ? { opacity: 0.7 } : undefined}>
                      <td className="mono">{it.sku_code}{it.is_component && <span className="tag-warn" style={{ marginLeft: 6 }} data-testid={`opname-component-flag-${it.sku_code}`}>KOMPONEN · tidak dapat diopname</span>}{noBasis && <span className="tag-warn" style={{ marginLeft: 6 }} data-testid={`opname-nobasis-flag-${it.sku_code}`}>HPP / cost basis tidak ditemukan</span>}</td>
                      <td>{it.description}</td>
                      <td className="mono">{it.is_component ? "—" : it.system_qty_snapshot}</td>
                      <td className="mono">{it.is_component ? "—" : it.physical_qty}</td>
                      <td className="mono" data-testid={`opname-variance-${it.sku_code}`}>{it.is_component ? "—" : (it.variance_qty > 0 ? `+${it.variance_qty}` : it.variance_qty)}</td>
                      <td>{it.is_component ? "—" : reasonLabel(it.reason)}</td>
                      <td>{it.note || "-"}</td>
                      {isDraft && (
                        <td style={{ whiteSpace: "nowrap" }}>
                          <button className="btn-mini" onClick={() => editItemQty(it)} data-testid={`opname-edit-${it.sku_code}`}>Ubah Qty</button>
                          <button className="btn-mini" style={{ marginLeft: 6 }} onClick={() => deleteItem(it)} data-testid={`opname-delete-${it.sku_code}`}>Hapus</button>
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {isDraft && (
              <div className="form-actions" style={{ marginTop: 10 }}>
                <button className="btn-primary" onClick={postOpname} disabled={(current.items || []).length === 0} data-testid="opname-post-button">Posting Opname</button>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
